/**
 * membershipWindow.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SKPLATFORM-UICC-001 CH-U2 — rendering an employment window.
 *
 * skplatform-api stopped deleting memberships (SKPLATFORM-CC-002 CH-3): each
 * row is one window, `joined → deactivated`, and a person who leaves and
 * returns has two. The console is where that history becomes visible — which
 * matters because CH-U2 also mandates the confirm copy "Their history is
 * retained", and that sentence is only true if the history is rendered
 * somewhere the person can see.
 */

export type MemberStatusFilter = 'active' | 'inactive' | 'all';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * `12 Mar 2025`.
 *
 * Day-month-year with a NAMED month, never the numeric form: `03/04/2025` is
 * 3 April to this console's users and 4 March to a US reader, and this string
 * is read to answer "did they have access in March?".
 *
 * Returns null rather than throwing on junk — one malformed row must not blank
 * the whole members table.
 */
function formatDay(value: string | Date | undefined | null): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * The window string CH-U2 specifies:
 *   open   → `joined 12 Mar 2025`
 *   closed → `joined 12 Mar 2025 → deactivated 20 Aug 2026`
 *
 * An open window gets no synthetic end ("→ present"): the absence of an end
 * date is a fact about the data, and inventing a word for it makes it look
 * stored. A closed window whose end date will not parse renders
 * `deactivated (unknown)` rather than dropping the clause — dropping it would
 * render a former member as someone who still has access, which is the one
 * mistake this screen must not make.
 */
export function formatMembershipWindow(
  member: { joinedAt?: string | Date | null; deactivatedAt?: string | Date | null } | null | undefined,
): string {
  if (!member || typeof member !== 'object') return '';

  const joined = formatDay(member.joinedAt);
  if (!joined) return '';

  if (!member.deactivatedAt) return `joined ${joined}`;

  const left = formatDay(member.deactivatedAt);
  return `joined ${joined} → deactivated ${left ?? '(unknown)'}`;
}

/**
 * CH-U2's Active / Inactive / All filter, defaulting to Active.
 *
 * A status this console does not recognise is NOT active. Fail closed on the
 * default view: a row that cannot be classified must not be presented as
 * someone who currently has access.
 */
export function matchesMemberStatusFilter(
  member: { status?: string } | null | undefined,
  filter: MemberStatusFilter,
): boolean {
  if (filter === 'all') return true;
  const isActive = member?.status === 'active';
  return filter === 'active' ? isActive : !isActive;
}
